import { AxiosError } from 'axios';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import Input from '../../../../../components/Input';
import Modal from '../../../../../components/Modal';
import {
  backendErrorNotification,
  frontendNotification
} from '../../../../../components/Notification';
import HttpStatus from '../../../../../enums/httpStatus';
import { resetUserPasswordSuperadminReq } from '../../../../../services/requests';
import {
  isValidPassword,
  isValidPasswordConfirmation,
} from '../../../../../utils/validations';
import { User } from '../../../../../types/apiResponse/user';

type Props = {
  showModal: boolean;
  closeModal: () => void;
  callback: () => void;
  userSelected: User | null;
};

export const ResetPasswordModal = ({
  showModal,
  closeModal,
  callback,
  userSelected,
}: Props) => {
  const { t } = useTranslation();

  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const validatePassword = (): boolean => {
    if (!password) {
      frontendNotification({
        message: t('passwordIsRequired'),
        type: 'warning',
      });
      return false;
    }

    if (!isValidPassword(password)) {
      frontendNotification({
        message: t('invalidPassword'),
        type: 'warning',
      });
      return false;
    }

    if (!passwordConfirm) {
      frontendNotification({
        message: t('passwordConfirmationIsRequired'),
        type: 'warning',
      });
      return false;
    }

    if (!isValidPasswordConfirmation(password, passwordConfirm)) {
      frontendNotification({
        message: t('passwordsDoNotMatch'),
        type: 'warning',
      });
      return false;
    }

    return true;
  };

  const resetPasswordAction = async () => {
    if (!userSelected) return;
    if (!validatePassword()) return;

    setIsLoading(true);

    try {
      const data = {
        password,
        password_confirmation: passwordConfirm,
      };

      const response = await resetUserPasswordSuperadminReq.update(data, userSelected.id);

      if (response.status === HttpStatus.OK) {
        frontendNotification({
          message: t('passwordResetSuccess'),
          type: 'success',
        });

        callback();
        closeModal();
      } else {
        throw response;
      }
    } catch (err) {
      backendErrorNotification(err as AxiosError<any, any>);
    } finally {
      setIsLoading(false);
    }
  };

  if (!userSelected) return null;

  return (
    <Modal
      title={t('resetPassword')}
      showModal={showModal}
      closeModal={closeModal}
      action={() => resetPasswordAction()}
      actionNameBtn={t('confirm')}
      isLoading={isLoading}
      bodyStyle={{ minWidth: '400px' }}
    >
      <div style={{ padding: '1rem' }}>
        <div style={{ marginBottom: '1.5rem' }}>
          <p style={{ marginBottom: '0.5rem' }}>
            <strong>{t('user')}:</strong> {userSelected.name} ({userSelected.username})
          </p>
          <p style={{ marginBottom: '0.5rem' }}>
            <strong>{t('email')}:</strong> {userSelected.email}
          </p>
          <p style={{ color: '#666', fontSize: '0.9rem' }}>
            {t('resetPasswordInfo')}
          </p>
        </div>

        <div className="row">
          <div className="itemFormWithLabel">
            <Input
              value={password}
              onChange={(e: any) => setPassword(e.target.value)}
              label={t('newPassword')}
              type="password"
              showPswBtn
              validator={isValidPassword}
              errorMsg={t('invalidPassword')}
              required={t('required')}
              disabled={isLoading}
            />
          </div>
        </div>

        <div className="row">
          <div className="itemFormWithLabel">
            <Input
              value={passwordConfirm}
              onChange={(e: any) => setPasswordConfirm(e.target.value)}
              label={t('confirmPassword')}
              type="password"
              showPswBtn
              isMatch={password}
              errorMsg={t('passwordsDoNotMatch')}
              required={t('required')}
              disabled={isLoading}
            />
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default ResetPasswordModal;
